import React, { useMemo, useState } from 'react';
import { Broom } from '@phosphor-icons/react';
import { useLanguage } from '../context/LanguageContext';

const L = {
  el: {
    month: 'Μήνας', year: 'Έτος', allMonths: 'Όλοι οι μήνες', allYears: 'Όλα τα έτη',
    totalJobs: 'Σύνολο Εργασιών', totalHours: 'Σύνολο Ωρών', totalCost: 'Συνολικό Κόστος Καθαριστριών', lastJob: 'Τελευταία Εργασία',
    date: 'Ημερομηνία', cleaner: 'Καθαρίστρια', desc: 'Περιγραφή Εργασίας', hours: 'Ώρες', cost: 'Κόστος Καθαρίστριας',
    total: 'Σύνολο', none: 'Δεν υπάρχουν εργασίες για την επιλεγμένη περίοδο', byCleaner: 'Ανά καθαρίστρια', loading: 'Φόρτωση…',
    months: ['Ιανουάριος', 'Φεβρουάριος', 'Μάρτιος', 'Απρίλιος', 'Μάιος', 'Ιούνιος', 'Ιούλιος', 'Αύγουστος', 'Σεπτέμβριος', 'Οκτώβριος', 'Νοέμβριος', 'Δεκέμβριος'],
  },
  en: {
    month: 'Month', year: 'Year', allMonths: 'All Months', allYears: 'All Years',
    totalJobs: 'Total Jobs', totalHours: 'Total Hours', totalCost: 'Total Cleaner Cost', lastJob: 'Last Job Date',
    date: 'Date', cleaner: 'Cleaner', desc: 'Job Description', hours: 'Hours', cost: 'Cleaner Cost',
    total: 'Total', none: 'No jobs for the selected period', byCleaner: 'By cleaner', loading: 'Loading…',
    months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  },
};

const r2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
const fmtD = (d) => (d ? d.slice(0, 10).split('-').reverse().join('/') : '—');
const hoursOf = (j) => Number(j.total_hours ?? j.hours_worked) || 0;
const costOf = (j) => Number(j.total_cleaner_cost ?? j.cleaner_earned) || 0;

const Kpi = ({ label, value, testId, tone = 'text-zinc-900' }) => (
  <div className="px-4 py-3 border-r border-zinc-100 last:border-r-0">
    <div className="text-[9px] font-semibold uppercase tracking-wider text-zinc-500">{label}</div>
    <div className={`text-lg font-mono font-bold tabular-nums ${tone}`} data-testid={testId}>{value}</div>
  </div>
);

const Filters = ({ s, month, year, years, setMonth, setYear, p }) => (
  <div className="flex items-center gap-2 px-4 py-2 border-b border-zinc-100 bg-zinc-50/50 text-xs flex-wrap">
    <span className="text-[11px] text-zinc-500">{s.month}:</span>
    <select value={month} onChange={(e) => setMonth(e.target.value)} className="h-7 border border-zinc-300 bg-white px-2 text-xs" data-testid={`${p}-month-filter`}>
      <option value="all">{s.allMonths}</option>
      {s.months.map((m, i) => <option key={m} value={String(i + 1).padStart(2, '0')}>{m}</option>)}
    </select>
    <span className="text-[11px] text-zinc-500 ml-2">{s.year}:</span>
    <select value={year} onChange={(e) => setYear(e.target.value)} className="h-7 border border-zinc-300 bg-white px-2 text-xs" data-testid={`${p}-year-filter`}>
      <option value="all">{s.allYears}</option>
      {years.map((y) => <option key={y} value={y}>{y}</option>)}
    </select>
  </div>
);

const CleanerChips = ({ rows, s, p }) => {
  const by = {};
  rows.forEach((j) => {
    const k = j.cleaner_names || '—';
    by[k] = by[k] || { hours: 0, cost: 0, jobs: 0 };
    by[k].hours += hoursOf(j); by[k].cost += costOf(j); by[k].jobs += 1;
  });
  const list = Object.entries(by).sort((a, b) => b[1].cost - a[1].cost);
  if (!list.length) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap px-4 py-2 border-b border-zinc-100" data-testid={`${p}-cleaner-summary`}>
      <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">{s.byCleaner}:</span>
      {list.map(([name, v]) => (
        <span key={name} className="text-[11px] border border-zinc-200 bg-white px-2 py-0.5 font-mono">
          <b className="font-sans">{name}</b> · {v.jobs} · {r2(v.hours).toFixed(2)}h · <span className="text-amber-700">€{r2(v.cost).toFixed(2)}</span>
        </span>
      ))}
    </div>
  );
};

export const ClientJobsPanel = ({ jobs = [], loading = false, testIdPrefix = 'client-jobs' }) => {
  const { language } = useLanguage();
  const s = L[language] || L.en;
  const p = testIdPrefix;
  const [month, setMonth] = useState('all');
  const [year, setYear] = useState('all');
  const years = useMemo(() => [...new Set(jobs.map((j) => (j.date || '').slice(0, 4)).filter(Boolean))].sort().reverse(), [jobs]);
  const rows = useMemo(() => jobs
    .filter((j) => (year === 'all' || (j.date || '').slice(0, 4) === year) && (month === 'all' || (j.date || '').slice(5, 7) === month))
    .sort((a, b) => (b.date || '').localeCompare(a.date || '')), [jobs, month, year]);
  const hours = r2(rows.reduce((a, j) => a + hoursOf(j), 0));
  const cost = r2(rows.reduce((a, j) => a + costOf(j), 0));
  const last = rows.length ? rows[0].date : null;

  if (loading) return <div className="p-6 text-sm text-zinc-400" data-testid={`${p}-loading`}>{s.loading}</div>;
  return (
    <div data-testid={`${p}-panel`}>
      <Filters s={s} month={month} year={year} years={years} setMonth={setMonth} setYear={setYear} p={p} />
      <div className="grid grid-cols-2 md:grid-cols-4 border-b border-zinc-200" data-testid={`${p}-kpis`}>
        <Kpi label={s.totalJobs} value={rows.length} testId={`${p}-kpi-jobs`} />
        <Kpi label={s.totalHours} value={hours.toFixed(2)} testId={`${p}-kpi-hours`} />
        <Kpi label={s.totalCost} value={`€${cost.toFixed(2)}`} testId={`${p}-kpi-cost`} tone="text-amber-800" />
        <Kpi label={s.lastJob} value={fmtD(last)} testId={`${p}-kpi-last`} />
      </div>
      <CleanerChips rows={rows} s={s} p={p} />
      <div className="p-4 overflow-x-auto">
        <table className="w-full text-sm bg-white border border-zinc-200" data-testid={`${p}-table`}>
          <thead><tr className="bg-zinc-50 border-b text-[10px] text-zinc-500 uppercase tracking-wider">
            <th className="text-left px-3 py-2">{s.date}</th>
            <th className="text-left px-3 py-2">{s.cleaner}</th>
            <th className="text-left px-3 py-2">{s.desc}</th>
            <th className="text-right px-3 py-2">{s.hours}</th>
            <th className="text-right px-3 py-2">{s.cost}</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-12 text-zinc-400" data-testid={`${p}-empty`}><Broom size={28} className="mx-auto mb-2 text-zinc-300" />{s.none}</td></tr>
            ) : rows.map((j) => (
              <tr key={j._flat_id || j.job_id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`${p}-row-${j._flat_id || j.job_id}`}>
                <td className="px-3 py-2 font-mono">{fmtD(j.date)}</td>
                <td className="px-3 py-2 text-zinc-800">{j.cleaner_names || '—'}</td>
                <td className="px-3 py-2 text-zinc-600">{j.notes || j.description || '—'}</td>
                <td className="px-3 py-2 text-right font-mono">{hoursOf(j).toFixed(2)}</td>
                <td className="px-3 py-2 text-right font-mono text-amber-700">€{costOf(j).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot><tr className="border-t-2 border-zinc-300 bg-zinc-50 font-semibold">
              <td colSpan={3} className="px-3 py-2 text-right text-[10px] uppercase text-zinc-500">{s.total}</td>
              <td className="px-3 py-2 text-right font-mono" data-testid={`${p}-total-hours`}>{hours.toFixed(2)}</td>
              <td className="px-3 py-2 text-right font-mono text-amber-700" data-testid={`${p}-total-cost`}>€{cost.toFixed(2)}</td>
            </tr></tfoot>
          )}
        </table>
      </div>
    </div>
  );
};
